import { ref, onUnmounted, computed, watch } from 'vue';
import { useToast } from 'primevue/usetoast';

export const DEFAULT_WARN_AT_SECONDS = 60; // 1 minute

export interface InactivityAuthStore {
  isLoggedIn: boolean;
  sessionActiveUntil: number | null;
  redirectToLogin: (options?: { mode?: 'fresh' | 'resume' }) => void | Promise<void>;
}

export function useInactivityTimer({
  warnAtSeconds = DEFAULT_WARN_AT_SECONDS,
  authStore,
}: {
  warnAtSeconds?: number;
  authStore: InactivityAuthStore;
}) {
  const toast = useToast();

  const inactivityTime = ref<number | null>(null);
  const isSessionExpired = ref(false);
  const timerRef = ref<ReturnType<typeof setInterval> | null>(null);

  const getRemainingSeconds = () => {
    if (authStore.sessionActiveUntil === null) {
      return null;
    }

    return Math.max((authStore.sessionActiveUntil - Date.now()) / 1000, 0);
  };

  function tick() {
    const remainingSeconds = getRemainingSeconds();
    inactivityTime.value = remainingSeconds;

    if (remainingSeconds === null) {
      isSessionExpired.value = false;
      stop();
      return false;
    }

    if (remainingSeconds <= 0) {
      inactivityTime.value = 0;
      isSessionExpired.value = true;
      stop();
      return false;
    }

    isSessionExpired.value = false;

    return true;
  }

  function start() {
    if (timerRef.value || authStore.sessionActiveUntil === null) return;
    timerRef.value = setInterval(tick, 1000);
  }

  function stop() {
    if (timerRef.value) {
      clearInterval(timerRef.value);
      timerRef.value = null;
    }
  }

  watch(
    () => [authStore.isLoggedIn, authStore.sessionActiveUntil] as const,
    ([loggedIn, sessionActiveUntil]) => {
      if (!loggedIn || sessionActiveUntil === null) {
        inactivityTime.value = null;
        isSessionExpired.value = false;
        stop();
        return;
      }

      const shouldStartTimer = tick();

      if (shouldStartTimer) {
        start();
      }
    },
    { immediate: true },
  );

  onUnmounted(() => {
    stop();
  });

  const hasSessionDeadline = computed(() => authStore.sessionActiveUntil !== null);
  const isSessionExpiringSoon = computed(() => {
    return (
      inactivityTime.value !== null &&
      inactivityTime.value > 0 &&
      inactivityTime.value <= warnAtSeconds
    );
  });

  watch(isSessionExpiringSoon, (now, prev) => {
    if (now && !prev) {
      toast.add({
        severity: 'warn',
        summary: 'Session expiring soon',
        detail: `For your security, you will be signed out in ${warnAtSeconds} seconds unless your session is extended.`,
      });
    }
  });

  return {
    inactivityTime,
    hasSessionDeadline,
    isSessionExpiringSoon,
    isSessionExpired,
  };
}
